// Small DOM helpers shared by the editor and its UI.

/**
 * Creates an element: h('button', {class: 'x', onclick: fn, dataset: {a: 1}}, child, 'text', ...).
 * Attribute values are always set through the DOM, never through HTML strings.
 */
export function h(tag, attrs = {}, ...children) {
    return create(document, tag, attrs, children);
}

/** Same as h(), bound to another document (the canvas iframe). */
export function hFor(doc) {
    return (tag, attrs = {}, ...children) => create(doc, tag, attrs, children);
}

function create(doc, tag, attrs, children) {
    const el = doc.createElement(tag);
    for (const [key, value] of Object.entries(attrs || {})) {
        if (value === undefined || value === null || value === false) continue;
        if (key === 'class') el.className = value;
        else if (key === 'dataset') Object.assign(el.dataset, value);
        else if (key === 'style' && typeof value === 'object') Object.assign(el.style, value);
        else if (key === 'html') el.innerHTML = value; // trusted markup only (icons, templates)
        else if (key.startsWith('on') && typeof value === 'function') el.addEventListener(key.slice(2), value);
        else if (key in el && typeof value !== 'string' && key !== 'list') el[key] = value;
        else el.setAttribute(key, value === true ? '' : String(value));
    }
    append(el, children);
    return el;
}

function append(el, children) {
    for (const child of children.flat(Infinity)) {
        if (child === null || child === undefined || child === false) continue;
        // Nodes of the canvas iframe are not instances of this window's Node.
        el.append(typeof child === 'object' && typeof child.nodeType === 'number' ? child : String(child));
    }
}

/** Builds an SVG icon from the inner markup of a 256×256 Phosphor icon. */
export function icon(markup, className = '') {
    const span = document.createElement('span');
    span.className = 'pf-icon' + (className ? ' ' + className : '');
    span.setAttribute('aria-hidden', 'true');
    span.innerHTML = `<svg viewBox="0 0 256 256" fill="currentColor" focusable="false">${markup || ''}</svg>`;
    return span;
}

export function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

export function classList(el) {
    return String(el.getAttribute('class') || '').split(/\s+/).filter(Boolean);
}

/** Replaces every class matching `pattern` by `next` (string, array or empty). */
export function replaceClasses(el, pattern, next) {
    const kept = classList(el).filter(name => !pattern.test(name));
    const added = (Array.isArray(next) ? next : String(next || '').split(/\s+/)).filter(Boolean);
    const all = [...new Set([...kept, ...added])];
    if (all.length) el.setAttribute('class', all.join(' '));
    else el.removeAttribute('class');
}

export function findClass(el, pattern) {
    return classList(el).find(name => pattern.test(name)) || '';
}

export function debounce(fn, wait) {
    let timer = null;
    const debounced = function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => {
            timer = null;
            fn.apply(this, args);
        }, wait);
    };
    debounced.flush = function (...args) {
        if (timer !== null) {
            clearTimeout(timer);
            timer = null;
            fn.apply(this, args);
        }
    };
    debounced.cancel = () => {
        clearTimeout(timer);
        timer = null;
    };
    debounced.pending = () => timer !== null;
    return debounced;
}

/** Resolves an element from a selector, an element or a jQuery object. */
export function resolveElement(target, doc = document) {
    if (!target) return null;
    if (typeof target === 'string') return doc.querySelector(target);
    if (target.jquery) return target[0] || null;
    return target.nodeType === 1 ? target : null;
}

export function isElement(node) {
    return !!node && node.nodeType === 1;
}

/** Element children only, skipping the editor's own nodes. */
export function childBlocks(el) {
    return Array.from(el.children).filter(child => child.hasAttribute('data-pf'));
}

export function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

let uid = 0;
export function nextId(prefix = 'pf') {
    uid += 1;
    return `${prefix}-${Date.now().toString(36)}-${uid}`;
}
